import React from "react";
import { motion } from "framer-motion";
import { InView, EyebrowLabel, InlineCta } from "../utils/Animation";

// ─── Data ─────────────────────────────────────────────────────────────
const VALUES = [
  {
    title: "Resilience by Design",
    body: "Every product we build is engineered to withstand the realities of the Nigerian infrastructure environment — network instability, varied hardware, and unpredictable load.",
  },
  {
    title: "Delivery, Not Promises",
    body: "We are a build-first organisation. We earn trust by shipping, and we hold ourselves to the same standards we'd hold any product partner.",
  },
  {
    title: "World-Class Standards",
    body: "Being Nigerian-built doesn't mean building to a local standard. We benchmark against the best global engineering practices on every project.",
  },
  {
    title: "Honest Collaboration",
    body: "We work best as genuine partners — not vendors. We tell you what we think, including when we think you're wrong.",
  },
];

const TEAM = [
  {
    name: "Adeolu Adegbemi",
    role: "Founder & CEO",
    bio: "6+ years building technology products for African markets. Founder of multiple Technology companies.",
    image: "/images/suit.png",
  },
  // {
  //   name: "Chidi Okonkwo",
  //   role: "Lead Engineer",
  //   bio: "Full-stack engineer specialising in MERN architecture and high-performance APIs. Open source contributor.",
  //   image: "/images/shirt.png",
  // },
  // {
  //   name: "Amara Eze",
  //   role: "Product Designer",
  //   bio: "Product design and UX research for mobile-first African audiences. Formerly at a Pan-African e-commerce company.",
  //   image: "/images/hero.png",
  // },
  // {
  //   name: "Tolu Adeyemi",
  //   role: "Mobile Lead",
  //   bio: "React Native specialist with five shipped apps on both stores. Passionate about offline-first architecture.",
  //   image: "/images/hero.png",
  // },
];

const MILESTONES = [
  {
    year: "2019",
    event: "Incubator Tech Limited incorporated in Lagos, Nigeria.",
  },
  {
    year: "2020",
    event:
      "First enterprise client — a Series B Nigerian fintech. Delivered their investor portal in eight weeks.",
  },
  {
    year: "2021",
    event:
      "Team doubles. Launched our first in-house product, an internal project management tool.",
  },
  {
    year: "2022",
    event:
      "Mechrack Mobile reaches 5,000 farmers across three states. Named in TechCabal's 'Ones to Watch'.",
  },
  {
    year: "2023",
    event: "Launch Kit Naija ships. 500 founders onboard in the first month.",
  },
  {
    year: "2024",
    event:
      "50th project delivered. Expanded service offering to include full CTO-as-a-Service.",
  },
  {
    year: "2025",
    event:
      "Launch Kit Naija surpasses 1,200 users. New office in Abuja. Pan-African expansion begins.",
  },
];

export default function About() {
  return (
    <>
      {/* Hero */}
      <div className="relative bg-itl-navy pt-40 pb-32 overflow-hidden">
        <img
          src="/images/pattern.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-itl-navy/60 to-itl-navy" />
        <div className="absolute left-6 lg:left-12 top-0 bottom-0 flex items-center pointer-events-none">
          <div className="h-[35%] w-px bg-white/10" />
        </div>
        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-12">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-4"
          >
            About Us
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.85,
              ease: [0.22, 1, 0.36, 1],
              delay: 0.25,
            }}
            className="font-serif text-5xl lg:text-7xl font-light text-white max-w-3xl leading-[1.06] mb-6"
          >
            Built in Nigeria.
            <br />
            <em className="italic">Built for the world.</em>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-white/55 text-lg max-w-xl leading-relaxed"
          >
            Incubator Tech Limited is a registered Nigerian technology company
            providing world-class web development, mobile app development, and
            technology products.
          </motion.p>
        </div>
      </div>

      {/* Mission */}
      <section className="bg-white py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <InView>
            <EyebrowLabel>Our Mission</EyebrowLabel>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-itl-navy leading-snug mb-6">
              To be Africa's most trusted technology delivery partner.
            </h2>
            <p className="text-gray-600 leading-relaxed text-[15px] mb-5">
              We exist because African founders, businesses, and organisations
              deserve access to the same quality of technology product
              development as any company in Silicon Valley or London — without
              the timezone friction, cultural mismatch, or premium pricing.
            </p>
            <p className="text-gray-600 leading-relaxed text-[15px]">
              Our approach combines globally battle-tested engineering practices
              with deep roots in the Nigerian market. We understand the
              infrastructure challenges, the regulatory landscape, and the users
              — because we're building here too.
            </p>
          </InView>
          <InView delay={0.15}>
            <div className="relative">
              <img
                src="/images/africa2.jpg"
                alt="The Incubator Tech team"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-itl-blue pointer-events-none" />
              <div className="absolute -top-6 -left-6 w-24 h-24 border border-gray-200 pointer-events-none" />
            </div>
          </InView>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#f7f7f5] py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView className="mb-14">
            <EyebrowLabel>What We Stand For</EyebrowLabel>
            <h2 className="font-serif text-4xl font-light text-itl-navy max-w-lg leading-snug">
              The principles that guide every decision we make.
            </h2>
          </InView>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200">
            {VALUES.map((v, i) => (
              <InView key={v.title} delay={i * 0.07} className="bg-white p-10">
                <div className="w-8 h-px bg-itl-blue mb-6" />
                <h3 className="font-serif text-2xl font-light text-itl-navy mb-4">
                  {v.title}
                </h3>
                <p className="text-gray-500 text-[14px] leading-relaxed">
                  {v.body}
                </p>
              </InView>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-white py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView className="mb-14">
            <EyebrowLabel>The Team</EyebrowLabel>
            <h2 className="font-serif text-4xl font-light text-itl-navy max-w-lg leading-snug">
              Engineers, designers, and product thinkers — all operators.
            </h2>
          </InView>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-100">
            {TEAM.map((member, i) => (
              <InView
                key={member.name}
                delay={i * 0.07}
                className="bg-white group"
              >
                <div className="relative overflow-hidden aspect-[3/4]">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-itl-navy/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                    <p className="text-white/80 text-[13px] leading-snug">
                      {member.bio}
                    </p>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-lg font-light text-itl-navy">
                    {member.name}
                  </h3>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-itl-blue mt-1">
                    {member.role}
                  </p>
                </div>
              </InView>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-itl-navy py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView className="mb-16">
            <EyebrowLabel>Our Story</EyebrowLabel>
            <h2 className="font-serif text-4xl font-light text-white max-w-lg leading-snug">
              Six years of building, shipping, and learning.
            </h2>
          </InView>
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[60px] top-0 bottom-0 w-px bg-white/10" />
            <div className="flex flex-col gap-0">
              {MILESTONES.map((m, i) => (
                <InView key={m.year} delay={i * 0.06}>
                  <div className="relative flex items-start gap-8 py-6">
                    {/* Year */}
                    <div className="w-[60px] shrink-0 text-right">
                      <span className="font-mono text-[13px] font-semibold text-itl-blue">
                        {m.year}
                      </span>
                    </div>
                    {/* Dot */}
                    <div className="absolute left-[56px] top-[30px] w-[9px] h-[9px] rounded-full bg-itl-blue border-2 border-itl-navy shrink-0" />
                    {/* Content */}
                    <div className="pl-6">
                      <p className="text-white/70 text-[14px] leading-relaxed">
                        {m.event}
                      </p>
                    </div>
                  </div>
                </InView>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Snapshot stats */}
      <section className="bg-[#f7f7f5] py-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-200">
            {[
              { value: "50+", label: "Projects Delivered" },
              { value: "20+", label: "Clients Served" },
              { value: "3", label: "In-House Products" },
              { value: "6", label: "Years in Operation" },
            ].map((s, i) => (
              <InView key={s.label} delay={i * 0.08}>
                <div className="px-8 py-10 text-center">
                  <p className="font-serif text-4xl font-light text-itl-navy mb-1">
                    {s.value}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400">
                    {s.label}
                  </p>
                </div>
              </InView>
            ))}
          </div>
        </div>
      </section>

      <InlineCta
        heading="Work with a team that treats your product like its own."
        body="Let's talk about what you're building — and how we can help you build it better, faster, and more resiliently."
        ctaLabel="Get In Touch"
        ctaHref="/contact"
      />
    </>
  );
}
